import * as THREE from 'three';
import { InstancedLayer } from './instancing';
import { assetUrl, isGenerated } from './library';
import type { Layout, LibraryAsset, PlacedItem, PlacementContext } from './types';

/**
 * Where an asset's geometry comes from. Generated assets have no file on the
 * CDN, so their id IS the source — running them through assetUrl() builds a
 * URL that 404s and the asset silently never places.
 */
function assetSource(asset: LibraryAsset): string {
    return isGenerated(asset.id) ? asset.id : assetUrl(asset);
}

// Authoring engine: brush scatter, precise click-place, an erase brush, an
// undo stack, and export to the baked-transform Layout format. Framework-free
// on purpose — the React panel (or any other UI) drives this through methods
// and reads state through the onChange callback, so the same core serves
// every project whatever its UI stack.

export interface BrushParams {
    /** Brush radius in metres. */
    radiusM: number;
    /** Items per stamp (a stamp fires every half-radius of drag travel). */
    perStamp: number;
    /**
     * Target size in METRES for the asset's height. Item scale is derived
     * from this against the asset's measured native bounds — pack assets are
     * authored in wildly different units and their own scale is never
     * trusted (see instancing.ts).
     */
    sizeM: number;
    /** Random multiplier range applied around sizeM. */
    jitterMin: number;
    jitterMax: number;
    rotationJitter: boolean;
}

export type EditorMode = 'brush' | 'place' | 'erase' | 'select' | 'off';

/** A placed item the user has grabbed, for transforming after the fact. */
export interface Selection {
    asset: string;
    index: number;
    /** Metres. */
    x: number;
    y: number;
    z: number;
    /** Degrees. */
    rotationDeg: number;
    /** Metres of the asset's height. */
    sizeM: number;
}

// Surfaces steeper than this reject a placement. hit.face.normal arrives in
// the MESH'S LOCAL frame — this model's ground meshes are authored Z-up, so
// the local y of level paving is 0 and testing it raw rejects everything (the
// same trap pedestrians.ts fell into). Always transform to world first.
const MIN_SURFACE_NORMAL_Y = 0.6;
// New items keep this fraction of their own target size away from siblings in
// the same layer, so a dense brush cannot stack trees inside each other.
const SPACING_FACTOR = 0.3;

type Stroke =
    | { type: 'add'; asset: string; count: number }
    | { type: 'erase'; removed: { asset: string; item: PlacedItem }[] };

interface LayerEntry {
    layer: InstancedLayer | null;
    items: PlacedItem[];
}

export class PlacementEditor {
    mode: EditorMode = 'off';
    brush: BrushParams = {
        radiusM: 8,
        perStamp: 3,
        sizeM: 8,
        jitterMin: 0.85,
        jitterMax: 1.2,
        rotationJitter: true,
    };
    /** Fixed yaw in DEGREES for click-place when jitter is off. */
    placeRotationDeg = 0;
    /** Item the select tool has grabbed, or null. */
    selection: Selection | null = null;

    private context: PlacementContext;
    private active: LibraryAsset | null = null;
    private layers = new Map<string, LayerEntry>();
    private strokes: Stroke[] = [];
    private group = new THREE.Group();

    private raycaster = new THREE.Raycaster();
    private pointer = new THREE.Vector2();
    private normalMatrix = new THREE.Matrix3();
    private worldNormal = new THREE.Vector3();
    private painting = false;
    private lastStamp = new THREE.Vector3();
    private onChange: () => void;

    private detach: () => void;

    // Brush-radius ring, following the surface under the cursor.
    private ring: THREE.Mesh;
    // Translucent stand-in of the active asset at the cursor in place mode.
    private ghost: THREE.Object3D | null = null;
    private ghostAsset: string | null = null;
    // Marks the item the select tool has grabbed.
    private selectionRing: THREE.Mesh;

    constructor(context: PlacementContext, onChange: () => void) {
        this.context = context;
        this.onChange = onChange;
        this.group.name = 'placement-editor';
        context.scene.add(this.group);

        // While the editor is open, the committed layout's runtime copy hides:
        // the editor imports those same items as editable layers, and showing
        // both would double every tree.
        const runtime = context.scene.getObjectByName('placement');
        if (runtime) runtime.visible = false;

        const ringGeometry = new THREE.RingGeometry(0.94, 1, 48);
        ringGeometry.rotateX(-Math.PI / 2);
        this.ring = new THREE.Mesh(
            ringGeometry,
            new THREE.MeshBasicMaterial({
                color: 0x6849fc,
                transparent: true,
                opacity: 0.85,
                depthTest: false,
                side: THREE.DoubleSide,
            }),
        );
        this.ring.renderOrder = 999;
        this.ring.visible = false;
        context.scene.add(this.ring);

        this.selectionRing = new THREE.Mesh(
            ringGeometry.clone(),
            new THREE.MeshBasicMaterial({
                color: 0x22d3ff,
                transparent: true,
                opacity: 0.95,
                depthTest: false,
                side: THREE.DoubleSide,
            }),
        );
        this.selectionRing.renderOrder = 1000;
        this.selectionRing.visible = false;
        context.scene.add(this.selectionRing);

        const el = context.domElement;
        const down = (e: PointerEvent) => this.pointerDown(e);
        const move = (e: PointerEvent) => this.pointerMove(e);
        const up = () => this.pointerUp();
        el.addEventListener('pointerdown', down);
        el.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        this.detach = () => {
            el.removeEventListener('pointerdown', down);
            el.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
    }

    /** True while a brush drag owns the pointer — the host should freeze its camera. */
    get isPainting(): boolean {
        return this.painting;
    }

    // ---- Model alignment ---------------------------------------------------
    // Total offset in metres on world axes. The runtime applies the SAVED
    // offset at boot, so the editor's baseline comes from the imported layout
    // and only deltas beyond it move the model live — re-applying the saved
    // part would shift the model twice.
    private modelOffset = new THREE.Vector3();

    get modelOffsetM(): [number, number, number] {
        return [this.modelOffset.x, this.modelOffset.y, this.modelOffset.z];
    }

    private modelRotation = 0;

    get modelRotationDeg(): number {
        return this.modelRotation;
    }

    /** Yaw the host's model by degrees, live, around its own origin. */
    rotateModel(deltaDeg: number): void {
        const model = this.context.model;
        if (!model) return;
        model.rotation.y += (deltaDeg * Math.PI) / 180;
        this.modelRotation += deltaDeg;
        this.onChange();
    }

    /** Move the host's model by metres on world axes, live. */
    nudgeModel(dxM: number, dyM: number, dzM: number): void {
        const model = this.context.model;
        if (!model) return;
        const upm = this.context.unitsPerMeter;
        model.position.x += dxM * upm;
        model.position.y += dyM * upm;
        model.position.z += dzM * upm;
        this.modelOffset.x += dxM;
        this.modelOffset.y += dyM;
        this.modelOffset.z += dzM;
        this.onChange();
    }

    setActiveAsset(asset: LibraryAsset | null): void {
        this.active = asset;
        // Load eagerly: the native measurements are needed before the first
        // item can be scaled, and waiting for the first click would swallow it.
        if (asset) void this.ensureLayer(asset);
    }

    get activeAsset(): LibraryAsset | null {
        return this.active;
    }

    counts(): { asset: string; count: number }[] {
        return [...this.layers.entries()]
            .filter(([, l]) => l.items.length > 0)
            .map(([asset, l]) => ({ asset, count: l.items.length }));
    }

    get canUndo(): boolean {
        return this.strokes.length > 0;
    }

    private ensureLayer(asset: LibraryAsset): Promise<LayerEntry> {
        const id = asset.id;
        let entry = this.layers.get(id);
        if (entry?.layer) return Promise.resolve(entry);
        if (!entry) {
            entry = { layer: null, items: [] };
            this.layers.set(id, entry);
        }
        return InstancedLayer.create(assetSource(asset), this.context.unitsPerMeter).then((layer) => {
            const current = this.layers.get(id)!;
            if (!current.layer) {
                current.layer = layer;
                this.group.add(layer.group);
                layer.setItems(current.items);
            }
            return current;
        });
    }

    // ---- Input -------------------------------------------------------------

    /**
     * First real surface along the ray: excluded groups are skipped, and the
     * hit must be walkably horizontal — a wall or a steep facade returns null
     * rather than letting an item embed itself in it. Taking the FIRST hit is
     * the "on top of the geometry" guarantee: the ray stops at the roof, so
     * nothing can land inside the building beneath it.
     */
    private firstSurface(hits: THREE.Intersection[]): THREE.Intersection | null {
        const excluded = this.context.excludeNames;
        outer: for (const hit of hits) {
            if (excluded) {
                let node: THREE.Object3D | null = hit.object;
                while (node) {
                    if (excluded.includes(node.name)) continue outer;
                    node = node.parent;
                }
            }
            const normal = hit.face?.normal;
            if (!normal) continue;
            this.normalMatrix.getNormalMatrix(hit.object.matrixWorld);
            this.worldNormal.copy(normal).applyMatrix3(this.normalMatrix).normalize();
            // The first non-excluded surface decides: if it is too steep the
            // placement fails, it does not fall through to whatever is behind
            // the wall.
            return this.worldNormal.y >= MIN_SURFACE_NORMAL_Y ? hit : null;
        }
        return null;
    }

    private groundHit(e: PointerEvent): THREE.Vector3 | null {
        const el = this.context.domElement;
        const rect = el.getBoundingClientRect();
        this.pointer.set(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1,
        );
        this.raycaster.setFromCamera(this.pointer, this.context.camera);
        const hit = this.firstSurface(
            this.raycaster.intersectObjects(this.context.groundTargets, true),
        );
        return hit ? hit.point : null;
    }

    /** Reject a candidate that would overlap a sibling in the same layer. */
    private tooClose(x: number, z: number): boolean {
        const entry = this.layers.get(this.active!.id);
        if (!entry) return false;
        const minSep = this.brush.sizeM * SPACING_FACTOR;
        const minSepSq = minSep * minSep;
        const upm = this.context.unitsPerMeter;
        const xM = x / upm;
        const zM = z / upm;
        for (const [ix, , iz] of entry.items) {
            const dx = ix - xM;
            const dz = iz - zM;
            if (dx * dx + dz * dz < minSepSq) return true;
        }
        return false;
    }

    private pointerDown(e: PointerEvent): void {
        if (this.mode === 'off' || e.button !== 0) return;
        if (this.mode === 'select') {
            this.selectAt(e);
            return;
        }
        if (this.mode !== 'erase' && !this.readyToPlace()) return;
        const hit = this.groundHit(e);
        if (!hit) return;

        if (this.mode === 'place') {
            if (this.tooClose(hit.x, hit.z)) return;
            this.strokes.push({ type: 'add', asset: this.active!.id, count: 0 });
            // Organic assets get random yaw and a few percent of size scatter:
            // identical rotation is the single clearest tell of placed props.
            // Manufactured ones (cars, furniture, lights) take the panel's
            // exact angle and exact size instead — a parked car at a random
            // heading is not variation, it is a mistake.
            const jitter = this.brush.rotationJitter;
            const rotation = jitter
                ? Math.random() * Math.PI * 2
                : (this.placeRotationDeg * Math.PI) / 180;
            this.addItem(hit, rotation, this.itemScale(jitter ? 0.92 + Math.random() * 0.16 : 1));
            this.refresh(this.active!.id);
            this.commitChange();
            return;
        }

        this.painting = true;
        if (this.mode === 'brush') {
            this.strokes.push({ type: 'add', asset: this.active!.id, count: 0 });
            this.stamp(hit);
        } else {
            this.strokes.push({ type: 'erase', removed: [] });
            this.eraseStamp(hit);
        }
        this.lastStamp.copy(hit);
    }

    private pointerMove(e: PointerEvent): void {
        this.moveCursor(e);
        if (!this.painting) return;
        const hit = this.groundHit(e);
        if (!hit) return;
        const spacing = (this.brush.radiusM / 2) * this.context.unitsPerMeter;
        if (hit.distanceTo(this.lastStamp) >= spacing) {
            if (this.mode === 'brush') this.stamp(hit);
            else this.eraseStamp(hit);
            this.lastStamp.copy(hit);
        }
    }

    private pointerUp(): void {
        if (!this.painting) return;
        this.painting = false;
        this.commitChange();
    }

    /** Ring in brush/erase, ghost in place — glued to the surface under the cursor. */
    private moveCursor(e: PointerEvent): void {
        const upm = this.context.unitsPerMeter;
        const hit = this.mode === 'off' ? null : this.groundHit(e);

        const showRing = !!hit && (this.mode === 'brush' || this.mode === 'erase');
        this.ring.visible = showRing;
        if (hit && showRing) {
            this.ring.position.copy(hit).y += 0.05 * upm;
            this.ring.scale.setScalar(this.brush.radiusM * upm);
            (this.ring.material as THREE.MeshBasicMaterial).color.set(
                this.mode === 'erase' ? 0xef4444 : 0x6849fc,
            );
        }

        const showGhost = !!hit && this.mode === 'place' && !!this.active;
        if (showGhost) void this.ensureGhost();
        if (this.ghost) {
            this.ghost.visible = showGhost && this.ghostAsset === this.active?.id;
            if (hit && this.ghost.visible) {
                this.ghost.position.copy(hit);
                const entry = this.layers.get(this.active!.id);
                if (entry?.layer) {
                    this.ghost.scale.setScalar(
                        (this.brush.sizeM / entry.layer.metrics.height) * upm,
                    );
                }
            }
        }
    }

    /** Build the translucent cursor stand-in for the active asset. */
    private async ensureGhost(): Promise<void> {
        if (!this.active || this.ghostAsset === this.active.id) return;
        this.ghostAsset = this.active.id;
        const { loadAsset, measureAsset } = await import('./instancing');
        const source = await loadAsset(assetSource(this.active));
        if (this.ghostAsset !== this.active?.id) return;
        this.disposeGhost();
        const ghost = source.clone();
        ghost.applyMatrix4(measureAsset(source).rebase);
        const wrapper = new THREE.Group();
        wrapper.add(ghost);
        wrapper.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (!mesh.isMesh) return;
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            // Cloned materials so the real instanced layer keeps its own.
            mesh.material = mats.map((m) => {
                const clone = m.clone() as THREE.Material & { opacity: number };
                clone.transparent = true;
                clone.opacity = 0.45;
                clone.depthWrite = false;
                return clone;
            }) as unknown as THREE.Material;
            if (!Array.isArray(mesh.material) && mats.length === 1) {
                mesh.material = (mesh.material as unknown as THREE.Material[])[0];
            }
        });
        wrapper.visible = false;
        this.ghost = wrapper;
        this.context.scene.add(wrapper);
    }

    private disposeGhost(): void {
        if (!this.ghost) return;
        this.ghost.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (!mesh.isMesh) return;
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const m of mats) m.dispose();
        });
        this.context.scene.remove(this.ghost);
        this.ghost = null;
    }

    private readyToPlace(): boolean {
        if (!this.active) return false;
        const entry = this.layers.get(this.active.id);
        if (!entry?.layer) {
            // Still measuring/loading the GLB; placing now would need a scale
            // we cannot compute yet.
            console.info('[placement] asset still loading, click again in a moment');
            return false;
        }
        return true;
    }

    /** Native→world scale for the current asset at the current target size. */
    private itemScale(jitter = 1): number {
        const entry = this.layers.get(this.active!.id)!;
        return (this.brush.sizeM / entry.layer!.metrics.height) * jitter;
    }

    // ---- Placement ---------------------------------------------------------

    private downProbe = new THREE.Raycaster();

    // ---- Select and transform ----------------------------------------------

    /**
     * Grab the placed item under the pointer.
     *
     * InstancedMesh raycasting hands back an instanceId, which indexes the
     * layer's item list directly — that is what makes an instanced layer
     * editable at all rather than a write-only blob.
     */
    private selectAt(e: PointerEvent): void {
        const el = this.context.domElement;
        const rect = el.getBoundingClientRect();
        this.pointer.set(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1,
        );
        this.raycaster.setFromCamera(this.pointer, this.context.camera);
        const hits = this.raycaster.intersectObject(this.group, true);
        for (const hit of hits) {
            const mesh = hit.object as THREE.InstancedMesh;
            if (!mesh.isInstancedMesh || hit.instanceId === undefined) continue;
            for (const [asset, entry] of this.layers) {
                if (!entry.layer?.group.children.includes(mesh)) continue;
                const item = entry.items[hit.instanceId];
                if (!item) continue;
                this.setSelection(asset, hit.instanceId);
                return;
            }
        }
        this.setSelection(null, 0);
    }

    private setSelection(asset: string | null, index: number): void {
        if (!asset) {
            this.selection = null;
            this.selectionRing.visible = false;
            this.onChange();
            return;
        }
        const entry = this.layers.get(asset)!;
        const [x, y, z, rotY, scale] = entry.items[index];
        this.selection = {
            asset,
            index,
            x,
            y,
            z,
            rotationDeg: (rotY * 180) / Math.PI,
            sizeM: scale * (entry.layer?.metrics.height ?? 1),
        };
        this.showSelectionRing();
        this.onChange();
    }

    private showSelectionRing(): void {
        if (!this.selection) return;
        const upm = this.context.unitsPerMeter;
        const { x, y, z, sizeM } = this.selection;
        this.selectionRing.visible = true;
        this.selectionRing.position.set(x * upm, y * upm + 0.05 * upm, z * upm);
        this.selectionRing.scale.setScalar(Math.max(sizeM * 0.6, 0.5) * upm);
    }

    /** Rewrite the selected item from the current selection values. */
    private commitSelection(): void {
        if (!this.selection) return;
        const { asset, index, x, y, z, rotationDeg, sizeM } = this.selection;
        const entry = this.layers.get(asset);
        if (!entry) return;
        const nativeHeight = entry.layer?.metrics.height ?? 1;
        entry.items[index] = [x, y, z, (rotationDeg * Math.PI) / 180, sizeM / nativeHeight];
        this.refresh(asset);
        this.showSelectionRing();
        this.onChange();
    }

    /** Move the selected item by metres on world axes. */
    moveSelection(dxM: number, dyM: number, dzM: number): void {
        if (!this.selection) return;
        this.selection.x += dxM;
        this.selection.y += dyM;
        this.selection.z += dzM;
        this.commitSelection();
    }

    /** Set the selected item's yaw in degrees. */
    setSelectionRotation(deg: number): void {
        if (!this.selection) return;
        this.selection.rotationDeg = deg;
        this.commitSelection();
    }

    /** Set the selected item's height in metres. */
    setSelectionSize(sizeM: number): void {
        if (!this.selection) return;
        this.selection.sizeM = sizeM;
        this.commitSelection();
    }

    /** Drop the selected item on the ground beneath it. */
    dropSelection(): void {
        if (!this.selection) return;
        const upm = this.context.unitsPerMeter;
        const { x, z } = this.selection;
        this.downProbe.set(
            new THREE.Vector3(x * upm, (this.selection.y + 100) * upm, z * upm),
            new THREE.Vector3(0, -1, 0),
        );
        this.downProbe.far = 400 * upm;
        const hit = this.firstSurface(
            this.downProbe.intersectObjects(this.context.groundTargets, true),
        );
        if (!hit) return;
        this.selection.y = hit.point.y / upm;
        this.commitSelection();
    }

    deleteSelection(): void {
        if (!this.selection) return;
        const { asset, index } = this.selection;
        const entry = this.layers.get(asset);
        if (!entry) return;
        const [removed] = entry.items.splice(index, 1);
        this.strokes.push({ type: 'erase', removed: [{ asset, item: removed }] });
        this.refresh(asset);
        this.setSelection(null, 0);
    }

    private stamp(center: THREE.Vector3): void {
        const upm = this.context.unitsPerMeter;
        for (let i = 0; i < this.brush.perStamp; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.sqrt(Math.random()) * this.brush.radiusM * upm;
            const x = center.x + Math.cos(angle) * radius;
            const z = center.z + Math.sin(angle) * radius;
            // Re-probe the ground at the sampled point: the brush disc can
            // overhang a terrace edge, and inheriting the cursor's height
            // would float or bury the item by the height of the step.
            if (this.tooClose(x, z)) continue;
            // Probe from well above the whole model, not from the cursor's
            // height: the topmost surface at this x/z must win, or a brush
            // centred on a plaza would drop samples through roofs beside it.
            this.downProbe.set(
                new THREE.Vector3(x, center.y + 200 * upm, z),
                new THREE.Vector3(0, -1, 0),
            );
            this.downProbe.far = 500 * upm;
            const hit = this.firstSurface(
                this.downProbe.intersectObjects(this.context.groundTargets, true),
            );
            if (!hit) continue;
            const rotation = this.brush.rotationJitter ? Math.random() * Math.PI * 2 : 0;
            const jitter =
                this.brush.jitterMin + Math.random() * (this.brush.jitterMax - this.brush.jitterMin);
            this.addItem(hit.point, rotation, this.itemScale(jitter));
        }
        this.refresh(this.active!.id);
    }

    private eraseStamp(center: THREE.Vector3): void {
        const upm = this.context.unitsPerMeter;
        const radius = this.brush.radiusM * upm;
        const stroke = this.strokes[this.strokes.length - 1];
        if (stroke?.type !== 'erase') return;
        for (const [asset, entry] of this.layers) {
            let removedAny = false;
            for (let i = entry.items.length - 1; i >= 0; i--) {
                const [x, , z] = entry.items[i];
                const dx = x * upm - center.x;
                const dz = z * upm - center.z;
                if (dx * dx + dz * dz <= radius * radius) {
                    stroke.removed.push({ asset, item: entry.items[i] });
                    entry.items.splice(i, 1);
                    removedAny = true;
                }
            }
            if (removedAny) this.refresh(asset);
        }
    }

    private addItem(world: THREE.Vector3, rotY: number, scale: number): void {
        const entry = this.layers.get(this.active!.id)!;
        const upm = this.context.unitsPerMeter;
        entry.items.push([world.x / upm, world.y / upm, world.z / upm, rotY, scale]);
        const stroke = this.strokes[this.strokes.length - 1];
        if (stroke?.type === 'add') stroke.count++;
    }

    private refresh(assetId: string): void {
        const entry = this.layers.get(assetId);
        if (entry?.layer) entry.layer.setItems(entry.items);
    }

    private commitChange(): void {
        const last = this.strokes[this.strokes.length - 1];
        if (last?.type === 'add' && last.count === 0) this.strokes.pop();
        if (last?.type === 'erase' && last.removed.length === 0) this.strokes.pop();
        this.onChange();
    }

    // ---- Operations --------------------------------------------------------

    undo(): void {
        const stroke = this.strokes.pop();
        if (!stroke) return;
        if (stroke.type === 'add') {
            const entry = this.layers.get(stroke.asset);
            if (entry) {
                entry.items.length = Math.max(0, entry.items.length - stroke.count);
                this.refresh(stroke.asset);
            }
        } else {
            const touched = new Set<string>();
            for (const { asset, item } of stroke.removed) {
                this.layers.get(asset)?.items.push(item);
                touched.add(asset);
            }
            for (const asset of touched) this.refresh(asset);
        }
        this.onChange();
    }

    clearLayer(assetId: string): void {
        const entry = this.layers.get(assetId);
        if (!entry) return;
        entry.items.length = 0;
        this.refresh(assetId);
        this.strokes = this.strokes.filter((s) =>
            s.type === 'add' ? s.asset !== assetId : true,
        );
        this.onChange();
    }

    /** Load an existing layout into the editor for further work. */
    async importLayout(layout: Layout, library: LibraryAsset[]): Promise<void> {
        if (layout.modelOffset) {
            // Baseline only — the runtime already moved the model at boot.
            this.modelOffset.set(...layout.modelOffset);
        }
        if (layout.modelRotationY) this.modelRotation = layout.modelRotationY;
        for (const { asset, items } of layout.layers) {
            const libraryAsset = library.find((a) => a.id === asset);
            if (!libraryAsset || !items.length) continue;
            const entry = await this.ensureLayer(libraryAsset);
            entry.items.push(...items);
            this.refresh(asset);
        }
        this.onChange();
    }

    exportLayout(site: string): Layout {
        const offset = this.modelOffset;
        return {
            version: 1,
            site,
            ...(offset.lengthSq() > 0
                ? { modelOffset: [offset.x, offset.y, offset.z] as [number, number, number] }
                : {}),
            ...(this.modelRotation !== 0 ? { modelRotationY: this.modelRotation } : {}),
            layers: [...this.layers.entries()]
                .filter(([, l]) => l.items.length > 0)
                .map(([asset, l]) => ({ asset, items: l.items })),
        };
    }

    dispose(): void {
        this.detach();
        this.disposeGhost();
        this.ring.geometry.dispose();
        (this.ring.material as THREE.Material).dispose();
        this.context.scene.remove(this.ring);
        this.selectionRing.geometry.dispose();
        (this.selectionRing.material as THREE.Material).dispose();
        this.context.scene.remove(this.selectionRing);
        for (const { layer } of this.layers.values()) layer?.dispose();
        this.context.scene.remove(this.group);
        // Hand the stage back to the committed layout.
        const runtime = this.context.scene.getObjectByName('placement');
        if (runtime) runtime.visible = true;
    }
}
